import React, { useState, FormEvent, FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFetchLayer, useUpdateFeature } from 'services/layer'
import { buildSimpleOptions, buildSimpleOption } from 'utils/selectOption'
import { FeatureMetaParams } from 'models/layer'
import { InfowindowData } from 'models/infowindow'
import { Dialog } from 'components/Dialog'
import { Flex } from 'components/Flex'
import { Stack } from 'components/Stack'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { Button } from 'components/Button'
import { Spinner } from 'components/Spinner'
import { SelectInput } from 'components/SelectInput'
import { TextArea } from 'components/TextArea'
import { TextInput } from 'components/TextInput'
import { Alert } from 'components/Alert'
type UpdateFeatureDialogProps = {
  infowindow: InfowindowData
  layerId: number
  infowindowId: number
  close: () => void
}
export const UpdateFeatureDialog: FC<UpdateFeatureDialogProps> = ({
  layerId,
  infowindowId,
  infowindow,
  close,
}) => {
  const { t } = useTranslation()
  const { layer, status } = useFetchLayer(layerId)
  const [form, setForm] = useState<FeatureMetaParams>({})
  const [updateFeature, { status: updateStatus }] = useUpdateFeature(layer?.id || -1, infowindowId)
  useEffect(() => {
    if (!layer || !layer.fields) return
    setForm(
      layer!.fields!.reduce(
        (object, field) => ({
          ...object,
          [field.key]: infowindow.data.find((data) => data.key === field.key)?.value,
        }),
        {},
      ),
    )
  }, [layer, infowindow])
  const onFormSubmit = (event: FormEvent) => {
    event.preventDefault()
    return updateFeature(
      { data: form },
      {
        onSuccess: () => { close(); window.location.reload() }
      },
    )
  }
  return (
    <Dialog aria-label={t(`infowindow_update.title`)} isOpen={true} onClose={close}>
      {status === 'loading' && (
        <Flex justify="center" align="center">
          <Spinner />
        </Flex>
      )}
      {layer && (
        <Stack>
          <header>
            <Flex justify="start" align="center">
              <Heading size="card">{t(`infowindow_update.title`)}</Heading>
            </Flex>
            <Text size="small" component="span">
              {t(`infowindow_update.desc`)}
            </Text>
          </header>
          <form onSubmit={onFormSubmit}>
            <Stack space="medium">
              {layer &&
                layer.fields?.map((field) => {
                  switch (field.type) {
                    case 'text':
                      return (
                        <TextInput
                          key={field.key}
                          name={field.key}
                          label={field.name}
                          onChange={(event) =>
                            setForm({ ...form, [field.key]: event.target.value })
                          }
                          value={form[field.key]}
                        />
                      )
                    case 'textarea':
                      return (
                        <TextArea
                          key={field.key}
                          name={field.key}
                          label={field.name}
                          onChange={(event) =>
                            setForm({ ...form, [field.key]: event.target.value })
                          }
                          value={form[field.key]}
                        />
                      )
                    case 'select':
                      return (
                        <SelectInput
                          key={field.key}
                          label={field.name}
                          options={buildSimpleOptions(field.options!)}
                          onChange={(value) =>
                            value ? setForm({ ...form, [field.key]: value.value }) : null
                          }
                          value={buildSimpleOption(form[field.key] || '')}
                        />
                      )
                  }
                })}
              <Button type="submit" full loading={updateStatus === 'loading'}>
                {t(`infowindow_update.cta`)}
              </Button>
              {updateStatus === 'error' && (
                <Alert full tone="critical">
                  {t('common.messages.error')}
                </Alert>
              )}
            </Stack>
          </form>
        </Stack>
      )}
    </Dialog>
  )
}